import { useState, useEffect } from 'react';
import { benchmarkService } from '../../services/benchmarkService';
import { JsonDiffViewer } from './JsonDiffViewer';
import { OcrTextViewer } from './OcrTextViewer';
import { LlmLogsViewer } from './LlmLogsViewer';
import { Modal, CollapsibleSection } from '../ui';
import type { DocumentDrilldown } from '../../types/benchmark';

const METRIC_TOOLTIPS = {
  precision: `De ce que le LLM a extrait, quel % est correct ?

Précision = matched / (matched + extra)

• matched = champs où LLM = Ground Truth
• extra = champs que le LLM a remplis mais qui sont null dans le Ground Truth

→ Une précision basse indique que le LLM "invente" des valeurs`,
  recall: `De ce qui devait être extrait, quel % a été trouvé ?

Rappel = matched / (matched + missing)

• matched = champs où LLM = Ground Truth
• missing = champs présents dans le Ground Truth mais null dans l'extraction LLM

→ Un rappel bas indique que le LLM "oublie" des valeurs`,
  f1: `Moyenne harmonique de Précision et Rappel

F1 = 2 × (Précision × Rappel) / (Précision + Rappel)

Le F1 équilibre précision et rappel en une seule métrique.`,
  weighted: `Score prenant en compte l'importance des champs

Score = Σ(score × poids) / Σ(poids)

• Critique (poids 3): Champs essentiels
• Standard (poids 2): Champs importants
• Mineur (poids 1): Champs secondaires
• Exclu (poids 0): Non comptabilisés

→ Reflète mieux la qualité métier de l'extraction`
};

interface DocumentDrilldownModalProps {
  isOpen: boolean;
  onClose: () => void;
  runId: string;
  documentId: number;
  onNavigate?: (direction: 'prev' | 'next') => void;
  canNavigatePrev?: boolean;
  canNavigateNext?: boolean;
}

export function DocumentDrilldownModal({
  isOpen,
  onClose,
  runId,
  documentId,
  onNavigate,
  canNavigatePrev,
  canNavigateNext,
}: DocumentDrilldownModalProps) {
  const [data, setData] = useState<DocumentDrilldown | null>(null);
  const [llmLogs, setLlmLogs] = useState<{ input: string; output: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [highlightedPage] = useState<number | undefined>(undefined);
  const [showOnlyErrors, setShowOnlyErrors] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState<Set<string> | null>(null);

  useEffect(() => {
    if (isOpen && documentId) {
      loadDrilldown();
      // Reset LLM logs when changing document
      setLlmLogs(null);
    }
  }, [isOpen, documentId, runId]);

  // Initialize collapsed sections when data changes (for new sections not yet tracked)
  useEffect(() => {
    if (data?.sections) {
      const sectionKeys = Object.keys(data.sections);
      setCollapsedSections(prev => {
        if (prev === null) {
          // First time: collapse all sections
          return new Set(sectionKeys);
        }
        // Keep existing collapsed state for sections that still exist
        // Sections that don't exist anymore are removed, existing state is preserved
        return prev;
      });
    }
  }, [data?.sections]);

  const loadDrilldown = async () => {
    try {
      setLoading(true);
      const drilldownData = await benchmarkService.getDocumentDrilldown(runId, documentId);
      setData(drilldownData);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load drilldown');
    } finally {
      setLoading(false);
    }
  };

  const loadLlmLogs = async () => {
    try {
      setError(null); // Clear any previous errors
      const logs = await benchmarkService.getLlmLogs(runId, documentId);
      setLlmLogs(logs);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load LLM logs';

      // Provide clearer error message for missing logs
      if (message.includes('store_llm_logs') || message.includes('not available')) {
        setError('Les logs LLM ne sont pas disponibles. Activez l\'option "Stocker logs LLM" lors de la création du run.');
      } else {
        setError(message);
      }
    }
  };

  // Navigation buttons for header
  const navigationButtons = onNavigate ? (
    <div className="flex gap-2">
      <button
        onClick={() => onNavigate('prev')}
        disabled={!canNavigatePrev}
        className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
      >
        ← Précédent
      </button>
      <button
        onClick={() => onNavigate('next')}
        disabled={!canNavigateNext}
        className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
      >
        Suivant →
      </button>
    </div>
  ) : undefined;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={data?.document.filename || 'Document'}
      size="lg"
      headerContent={navigationButtons}
    >
      {loading && (
        <div className="p-8 text-center">
          <div className="text-gray-500">Chargement...</div>
        </div>
      )}

      {/* Only show error at top if it's not about LLM logs (those are shown inline) */}
      {error && !error.includes('logs LLM') && (
        <div className="p-4 bg-red-50 border border-red-200 rounded mb-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {data && (
        <div className="space-y-6">
          {/* SECTION 1: Résultats (KPIs + Badges) - Non collapsible */}
          <div>
            {/* KPIs */}
            <div className="grid grid-cols-5 gap-3 mb-4">
              <div className="p-3 border rounded bg-blue-50">
                <div className="text-sm text-gray-600 flex items-center">
                  Précision
                  <span className="relative group ml-1">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-blue-600 cursor-help" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="absolute left-0 top-full mt-1 hidden group-hover:block z-[100] bg-gray-900 text-white text-sm rounded-lg shadow-lg p-3 w-72 whitespace-pre-wrap">
                      {METRIC_TOOLTIPS.precision}
                    </div>
                  </span>
                </div>
                <div className="text-2xl font-bold">{(data.scores.precision * 100).toFixed(1)}%</div>
              </div>
              <div className="p-3 border rounded bg-purple-50">
                <div className="text-sm text-gray-600 flex items-center">
                  Rappel
                  <span className="relative group ml-1">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-blue-600 cursor-help" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="absolute left-0 top-full mt-1 hidden group-hover:block z-[100] bg-gray-900 text-white text-sm rounded-lg shadow-lg p-3 w-72 whitespace-pre-wrap">
                      {METRIC_TOOLTIPS.recall}
                    </div>
                  </span>
                </div>
                <div className="text-2xl font-bold">{(data.scores.recall * 100).toFixed(1)}%</div>
              </div>
              <div className="p-3 border rounded bg-green-50">
                <div className="text-sm text-gray-600 flex items-center">
                  F1
                  <span className="relative group ml-1">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-blue-600 cursor-help" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="absolute left-0 top-full mt-1 hidden group-hover:block z-[100] bg-gray-900 text-white text-sm rounded-lg shadow-lg p-3 w-72 whitespace-pre-wrap">
                      {METRIC_TOOLTIPS.f1}
                    </div>
                  </span>
                </div>
                <div className="text-2xl font-bold">{data.scores.f1.toFixed(3)}</div>
              </div>
              {data.weighted_scoring && (
                <div className="p-3 border rounded bg-indigo-50">
                  <div className="text-sm text-gray-600 flex items-center">
                    Pondéré
                    <span className="relative group ml-1">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-blue-600 cursor-help" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div className="absolute left-0 top-full mt-1 hidden group-hover:block z-[100] bg-gray-900 text-white text-sm rounded-lg shadow-lg p-3 w-72 whitespace-pre-wrap">
                        {METRIC_TOOLTIPS.weighted}
                      </div>
                    </span>
                  </div>
                  <div className="text-2xl font-bold">{(data.weighted_scoring.final_score * 100).toFixed(1)}%</div>
                </div>
              )}
              <div className="p-3 border rounded bg-yellow-50">
                <div className="text-sm text-gray-600">Temps</div>
                <div className="text-2xl font-bold">{(data.document.processing_time_ms / 1000).toFixed(1)}s</div>
              </div>
            </div>

            {/* Badges */}
            <div className="flex gap-3 flex-wrap">
              <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded text-sm">
                Erreurs: {data.diff.filter(d => d.status !== 'match' && d.status !== 'both_null').length}
              </span>
              {data.document.ocr_quality_score && (
                <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded text-sm">
                  OCR: {(data.document.ocr_quality_score * 100).toFixed(0)}%
                </span>
              )}
              {data.document.ocr_cache_used && (
                <span className="px-3 py-1 bg-green-100 text-green-800 rounded text-sm">
                  Cache OCR
                </span>
              )}
            </div>
          </div>

          {/* SECTION 2: Comparaison - Non collapsible */}
          <div>
            <h3 className="text-lg font-semibold mb-3">Comparaison</h3>
            <JsonDiffViewer
              diff={data.diff}
              fieldInstructions={data.field_instructions}
              fieldSections={data.field_sections}
              sections={data.sections}
              documentFilename={data.document.filename}
              showOnlyErrors={showOnlyErrors}
              onShowOnlyErrorsChange={setShowOnlyErrors}
              collapsedSections={collapsedSections || undefined}
              onCollapsedSectionsChange={setCollapsedSections}
              availableFilters={['all', 'differences']}
            />
          </div>

          {/* SECTION 3: Texte OCR - Collapsible, fermé par défaut */}
          {data.ocr_text.pages.length > 0 && (
            <CollapsibleSection title="Texte OCR" defaultOpen={false}>
              <OcrTextViewer pages={data.ocr_text.pages} highlightedPage={highlightedPage} />
            </CollapsibleSection>
          )}

          {/* SECTION 4: Logs LLM - Collapsible, fermé par défaut */}
          <CollapsibleSection title="Logs LLM" defaultOpen={false}>
            {!llmLogs ? (
              <div>
                <button
                  onClick={loadLlmLogs}
                  className="px-4 py-2 bg-gray-100 border rounded hover:bg-gray-200"
                >
                  Charger les logs LLM
                </button>

                {/* Show error inline if logs failed to load */}
                {error && error.includes('logs LLM') && (
                  <div className="mt-3 p-4 bg-yellow-50 border border-yellow-200 rounded">
                    <p className="text-yellow-800">{error}</p>
                  </div>
                )}
              </div>
            ) : (
              <LlmLogsViewer input={llmLogs.input} output={llmLogs.output} />
            )}
          </CollapsibleSection>
        </div>
      )}

    </Modal>
  );
}
